import type { FC } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';

const chartBarHeights = [32, 58, 44, 76, 54, 88, 68, 96, 72, 84, 62, 91];

export const UsageOverviewSkeleton: FC = () => (
    <div
        aria-label='Loading usage overview'
        role='status'
        style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))' }}
    >
        {Array.from({ length: 16 }, (_, index) => (
            <div key={index} style={{ minWidth: 0 }}>
                <ButtonSkeleton block size='small' style={{ display: 'block', height: '0.75rem', width: '55%' }} />
                <ButtonSkeleton
                    block
                    size='small'
                    style={{ display: 'block', height: '1.25rem', marginTop: '0.5rem', width: '72%' }}
                />
            </div>
        ))}
    </div>
);

export const UsageEventTimelineSkeleton: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <div
            aria-label='Loading event timeline'
            role='status'
            style={{ height: '15rem', overflow: 'hidden', position: 'relative' }}
        >
            {[22, 46, 70].map((top) => (
                <div
                    aria-hidden='true'
                    key={top}
                    style={{
                        borderTop: `1px dashed ${token.colorBorderSecondary}`,
                        left: 0,
                        position: 'absolute',
                        right: 0,
                        top: `${top}%`,
                    }}
                />
            ))}
            <div
                style={{
                    alignItems: 'flex-end',
                    bottom: '1.75rem',
                    display: 'grid',
                    gap: '0.75rem',
                    gridTemplateColumns: `repeat(${chartBarHeights.length}, minmax(0, 1fr))`,
                    left: '0.75rem',
                    position: 'absolute',
                    right: '0.75rem',
                    top: '1rem',
                }}
            >
                {chartBarHeights.map((height, index) => (
                    <ButtonSkeleton
                        block
                        key={index}
                        size='small'
                        style={{ display: 'block', height: `${height}%`, minWidth: 0, width: '100%' }}
                    />
                ))}
            </div>
            <ButtonSkeleton
                block
                size='small'
                style={{
                    bottom: '0.25rem',
                    display: 'block',
                    height: '0.625rem',
                    left: '1rem',
                    position: 'absolute',
                    width: '94%',
                }}
            />
        </div>
    );
};

export const UsageTableSkeleton: FC = () => (
    <div aria-label='Loading resource activity table' role='status'>
        {Array.from({ length: 8 }, (_, index) => (
            <div
                key={index}
                style={{
                    alignItems: 'center',
                    display: 'grid',
                    gap: '1rem',
                    gridTemplateColumns: 'minmax(10rem, 1.4fr) repeat(4, minmax(6rem, 1fr))',
                    minHeight: '2.5rem',
                }}
            >
                {[72, 60, 42, 68, 64].map((width, columnIndex) => (
                    <ButtonSkeleton
                        block
                        key={columnIndex}
                        size='small'
                        style={{ display: 'block', height: '0.75rem', width: `${width}%` }}
                    />
                ))}
            </div>
        ))}
    </div>
);

export const UsageMapSkeleton: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <div
            aria-label='Loading event locations'
            role='status'
            style={{
                alignItems: 'center',
                backgroundColor: token.colorFillQuaternary,
                backgroundImage: `linear-gradient(${token.colorBorderSecondary} 1px, transparent 1px), linear-gradient(90deg, ${token.colorBorderSecondary} 1px, transparent 1px)`,
                backgroundSize: '3rem 3rem',
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                display: 'flex',
                height: '24rem',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {[
                { left: '28%', top: '38%' },
                { left: '47%', top: '55%' },
                { left: '66%', top: '32%' },
                { left: '74%', top: '64%' },
            ].map((position, index) => (
                <span key={index} style={{ position: 'absolute', ...position }}>
                    <AvatarSkeleton size={18} />
                </span>
            ))}
            <ButtonSkeleton size='small' style={{ height: '0.875rem', width: '22%' }} />
        </div>
    );
};
